import { useImagingContext } from 'domains/viewer/ViewportDre/modules/imaging/ImagingContext';
import { useViewType } from 'domains/viewer/ViewportDre/modules/state';
import { DEFAULT_ZOOM_VALUE, useSetZoom } from 'domains/viewer/ViewportDre/modules/useSetCamera';
import { calculateCameraParameters } from 'domains/viewer/ViewportDre/utils/math';
import { viewportDisplayConfigurationSelector } from 'domains/viewer/ViewportsConfigurations/state';

import { useContext } from 'react';
import { Contexts } from 'react-vtk-js';
import { atomFamily, useRecoilCallback } from 'recoil';
import { useViewCamera } from './useViewCamera';
import type { RecoilState } from 'recoil';
import type { Vector3 } from '@kitware/vtk.js/types';
import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';
import { useShouldAlignChestWallWithSideOfViewport } from 'hooks/useClassificationBasedMammoFeatures';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

export const isCameraInitializedState: (viewportId: string) => RecoilState<boolean> = atomFamily({
  key: 'viewer.dre.isCameraInitialized',
  default: false,
});

export const isMipDataReadyState: (viewportId: string) => RecoilState<boolean> = atomFamily({
  key: 'viewer.dre.isMipDataReadyState',
  default: false,
});

function applyCameraParameters(
  camera: vtkCamera,
  position: Vector3,
  focalPoint: Vector3,
  viewUp: Vector3
) {
  camera.setPosition(...position);
  camera.setFocalPoint(...focalPoint);
  camera.setViewUp(...viewUp);
}

export function useInitCamera(viewportId: string): (
  arg1?: Nullable<{
    reset?: boolean;
  }>
) => void {
  const view = useContext(Contexts.ViewContext);
  const viewType = useViewType();
  const setZoom = useSetZoom();
  const { imagingProvider } = useImagingContext();
  const camera = useViewCamera(view);
  const shouldAlignChestWall = useShouldAlignChestWallWithSideOfViewport();

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        options: Nullable<{
          reset?: boolean;
        }>
      ) => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });

        const reset = options?.reset ?? false;
        const renderer: vtkRenderer | null = view?.getRenderer()?.get() ?? null;
        if (
          imagingProvider == null ||
          view == null ||
          renderer == null ||
          renderer?.isDeleted() === true
        )
          return;

        const viewportDisplayConfig = snapshot
          .getLoadable(viewportDisplayConfigurationSelector(viewportDisplayConfigKey))
          .getValue();

        const params2d = viewportDisplayConfig?.params2d;
        // we need our zoom operation to complete before we try to calculate
        // the camera parameters

        await setZoom(
          reset ? DEFAULT_ZOOM_VALUE : (viewportDisplayConfig?.params2d?.zoom ?? DEFAULT_ZOOM_VALUE)
        );

        if (
          reset === false &&
          params2d != null &&
          params2d.position != null &&
          params2d.focalPoint != null &&
          params2d.viewUp != null
        ) {
          applyCameraParameters(camera, params2d.position, params2d.focalPoint, params2d.viewUp);
        } else {
          const { position, focalPoint, viewUp } = calculateCameraParameters(
            imagingProvider,
            view,
            renderer,
            viewType,
            viewportId,
            shouldAlignChestWall
          );
          applyCameraParameters(camera, position, focalPoint, viewUp);
        }

        view.getRenderer()?.get()?.resetCameraClippingRange();
        view.requestRender();
        set(isCameraInitializedState(viewportId), true);
      },
    [viewportId, view, viewType, camera, imagingProvider, setZoom, shouldAlignChestWall]
  );
}
